import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { getClientById } from "../Apis/ClientAPIs";
import { formatDateUTC, formatDateTime } from "../helper/DateHelper";

// Helper to extract only the year from date/year strings
const getYearOnly = (val) => {
  if (!val) return "—";
  const cleaned = String(val).trim();
  if (cleaned.length === 4 && /^\d{4}$/.test(cleaned)) return cleaned;
  const d = new Date(val);
  return !isNaN(d.getFullYear()) ? String(d.getFullYear()) : cleaned;
};

const ClientViewLayer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchClientDetails = async () => {
            if (id) {
                setLoading(true);
                const res = await getClientById(id);
                if (res.status && res.response) {
                    setClient(res.response?.data || res.response);
                }
                setLoading(false);
            }
        };
        fetchClientDetails();
    }, [id]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!client) {
        return <div className="alert alert-danger">Exhibitor not found.</div>;
    }

    return (
        <div className="card h-100 p-0 radius-12 overflow-hidden border-0 shadow-sm">
            <div className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3" style={{ background: "linear-gradient(135deg, #000000, #4f1818, #ff0000)" }}>
                <div className="d-flex align-items-center gap-3">
                    <h6 className="text-white mb-0 fw-bold">Exhibitor Details: {client.companyName}</h6>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Link to={location.state?.from || (client?.groupId ? `/client-groups/view/${typeof client.groupId === 'object' ? (client.groupId?._id || client.groupId?.id) : client.groupId}` : "/clients")} className="btn btn-sm px-16 py-8 radius-8 d-flex align-items-center gap-2 shadow-sm fw-bold" style={{ backgroundColor: "#f7e7da", color: "black" }}>
                        <Icon icon="solar:arrow-left-bold" /> {location.state?.from ? "Back" : (client?.groupId ? "Back to Group" : "List Clients")}
                    </Link>
                </div>
            </div>

            <div className="card-body p-20">
                <div className="row gy-3">
                    {/* Basic Info Section */}
                    <div className="col-xl-4 col-lg-5">
                        <div className="bg-neutral-50 p-20 radius-12 border shadow-sm text-center">
                            {/* <div className="w-80-px h-80-px rounded-circle bg-primary-100 text-primary-600 d-flex align-items-center justify-content-center fw-bold text-xxl mx-auto mb-12 shadow-inner">
                                {client.keyPerson?.charAt(0).toUpperCase()}
                            </div> */}
                            <h6 className="mb-4 fw-bold">{client.keyPerson}</h6>
                            <p className="text-secondary-light mb-12 fw-semibold">{client.companyName}</p>

                            <div className="badge bg-danger-focus text-danger-main px-12 py-6 radius-pill fw-bold mb-16" style={{ fontSize: '12px' }}>
                                {client.clientType}
                            </div>

                            <div className="d-flex justify-content-center align-items-center gap-1 mb-16">
                                <Icon icon="solar:star-bold" className="text-warning text-lg" />
                                <span className="fw-semibold text-dark text-sm">
                                    {(() => {
                                        const r = String(client.ratings || "");
                                        const mapping = { "1": "VIP", "2": "ENQUIRY", "3": "OTHER EXHIBITOR", "4": "OWN EXHIBITOR" };
                                        return mapping[r] || r || "OTHER EXHIBITOR";
                                    })()}
                                </span>
                            </div>

                            <div className="text-start border-top pt-16">
                                <div className="d-flex align-items-center gap-3 mb-10">
                                    <Icon icon="solar:letter-bold" className="text-secondary-light text-lg" />
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div className="text-xxs text-secondary-light uppercase fw-bold">Email Address</div>
                                        <div className="text-sm fw-bold text-dark text-truncate" title={client.email}>
                                            {client.email || "N/A"}
                                        </div>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-3 mb-10">
                                    <Icon icon="solar:phone-bold" className="text-secondary-light text-lg" />
                                    <div>
                                        <div className="text-xs text-secondary-light uppercase fw-bold">Primary Mobile</div>
                                        <div className="text-sm fw-bold text-dark">{client.mobile1 || "N/A"}</div>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-3 mb-10">
                                    <Icon icon="solar:calendar-bold" className="text-secondary-light text-lg" />
                                    <div>
                                        <div className="text-xs text-secondary-light uppercase fw-bold">Customer Since</div>
                                        <div className="text-sm fw-bold">{getYearOnly(client.customerSince)}</div>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                    <Icon icon="solar:shield-check-bold" className="text-success text-lg" />
                                    <div>
                                        <div className="text-xs text-secondary-light uppercase fw-bold">Account Status</div>
                                        <div className={`text-sm fw-bold ${client.isActive !== false ? 'text-success' : 'text-danger'}`}>
                                            {client.isActive !== false ? 'ACTIVE' : 'INACTIVE'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact and Address Section */}
                    <div className="col-xl-8 col-lg-7">
                        <div className="row gy-3">
                            {/* Contact Details */}
                            <div className="col-md-6">
                                <div className="p-16 radius-12 border bg-white shadow-sm transition-hover">
                                    <h6 className="mb-12 border-bottom pb-8 d-flex align-items-center gap-2 text-primary-600 fw-bold" style={{ fontSize: '15px' }}>
                                        <Icon icon="solar:phone-bold" /> Contact Information
                                    </h6>
                                    <div className="row gy-3">
                                        {/* <div className="col-sm-6 detail-item">
                                            <label className="text-xxs text-secondary-light uppercase fw-bold mb-1 d-block">Email Address</label>
                                            <div className="text-xs fw-bold text-dark text-truncate" title={client.email}>{client.email}</div>
                                        </div>
                                        <div className="col-sm-6 detail-item">
                                            <label className="text-xxs text-secondary-light uppercase fw-bold mb-1 d-block">Primary Mobile</label>
                                            <div className="text-xs fw-bold text-dark">{client.mobile1}</div>
                                        </div> */}
                                        <div className="col-sm-6 detail-item">
                                            <label className="text-xs text-secondary-light uppercase fw-bold mb-1 d-block">Secondary Mobile</label>
                                            <div className="text-sm fw-bold text-dark text-truncate">{client.mobile2 || "N/A"}</div>
                                        </div>
                                        <div className="col-sm-6 detail-item">
                                            <label className="text-xs text-secondary-light uppercase fw-bold mb-1 d-block">Whatsapp</label>
                                            <div className="text-sm fw-bold text-success d-flex align-items-center gap-1">
                                                <Icon icon="ic:baseline-whatsapp" /> {client.whatsappNumber}
                                            </div>
                                        </div>
                                        <div className="col-sm-6 detail-item">
                                            <label className="text-xs text-secondary-light uppercase fw-bold mb-1 d-block">Landline</label>
                                            <div className="text-sm fw-bold text-dark text-truncate">{client.landline || "N/A"}</div>
                                        </div>
                                        <div className="col-sm-6 detail-item">
                                            <label className="text-xs text-secondary-light uppercase fw-bold mb-1 d-block">Website</label>
                                            <a href={client.websiteLink} target="_blank" rel="noreferrer" className="text-sm fw-bold text-primary-600 d-flex align-items-center gap-1 text-truncate">
                                                <Icon icon="solar:link-bold" /> {client.websiteLink ? client.websiteLink.replace('https://', '').replace('http://', '') : "N/A"}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Address Details */}
                            <div className="col-md-6">
                                <div className="p-16 radius-12 border bg-white shadow-sm transition-hover">
                                    <h6 className="mb-12 border-bottom pb-8 d-flex align-items-center gap-2 text-primary-600 fw-bold" style={{ fontSize: '15px' }}>
                                        <Icon icon="solar:map-point-bold" /> Location Details
                                    </h6>
                                    <div className="row gy-3">
                                        <div className="col-12 detail-item">
                                            <label className="text-xs text-secondary-light uppercase fw-bold mb-1 d-block">Address</label>
                                            <div className="text-sm fw-bold text-dark text-truncate">{client.address || "N/A"}</div>
                                        </div>
                                        {/* <div className="col-sm-6 detail-item">
                                            <label className="text-xxs text-secondary-light uppercase fw-bold mb-1 d-block">Area / Landmark</label>
                                            <div className="text-xs fw-bold text-dark">{client.area || "N/A"}</div>
                                        </div> */}
                                        <div className="col-sm-6 detail-item">
                                            <label className="text-xs text-secondary-light uppercase fw-bold mb-1 d-block">City</label>
                                            <div className="text-sm fw-bold text-dark text-truncate">
                                                {client.city || client.state ? `${client.city || ""}${client.city && client.state ? ", " : ""}${client.state || ""}` : "N/A"}
                                            </div>
                                        </div>
                                        <div className="col-sm-6 detail-item">
                                            <label className="text-xs text-secondary-light uppercase fw-bold mb-1 d-block">Pincode</label>
                                            <div className="text-sm fw-bold text-dark text-truncate">{client.pincode || "N/A"}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Business Interests */}
                            <div className="col-12">
                                <div className="p-20 radius-12 border bg-white shadow-sm transition-hover">
                                    <h6 className="mb-16 border-bottom pb-8 d-flex align-items-center gap-2 text-primary-600 fw-bold" style={{ fontSize: '15px' }}>
                                        <Icon icon="solar:tag-bold" /> Business Interests & Specializations
                                    </h6>

                                    <div className="row gy-4">
                                        <div className="col-md-6">
                                            <label className="text-xxs text-secondary-light uppercase fw-bold mb-12 d-flex align-items-center gap-2">
                                                <div className="w-3-px h-12-px bg-primary-600 radius-pill"></div>
                                                Tagged Products
                                            </label>
                                            <div className="row g-2">
                                                {client.products && client.products.length > 0 ? (
                                                    client.products.map((prod, idx) => (
                                                        <div key={idx} className="col-6">
                                                            <div className="p-10 radius-8 border bg-neutral-50 d-flex align-items-center gap-2 hover-border-primary-600 transition-2">
                                                                <Icon icon="solar:box-minimalistic-bold-duotone" className="text-primary-600" />
                                                                <div className="text-xs fw-bold text-dark" title={typeof prod === 'object' ? (prod.productName || prod.name) : prod}>
                                                                    {typeof prod === 'object' ? (prod.productName || prod.name) : prod}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="col-12"><div className="text-secondary-light italic text-xs">No products tagged.</div></div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <label className="text-xxs text-secondary-light uppercase fw-bold mb-12 d-flex align-items-center gap-2">
                                                <div className="w-3-px h-12-px bg-success-600 radius-pill"></div>
                                                Interested Locations
                                            </label>
                                            <div className="row g-2">
                                                {client.interestedLocations && client.interestedLocations.length > 0 ? (
                                                    client.interestedLocations.map((loc, idx) => (
                                                        <div key={idx} className="col-6">
                                                            <div className="p-10 radius-8 border bg-neutral-50 d-flex align-items-center gap-2 hover-border-success-600 transition-2">
                                                                <Icon icon="solar:map-point-wave-bold-duotone" className="text-success-600" />
                                                                <div className="text-xs fw-bold text-dark" title={typeof loc === 'object' ? (loc.name || loc.address) : loc}>
                                                                    {typeof loc === 'object' ? (loc.name || loc.address) : loc}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="col-12"><div className="text-secondary-light italic text-xs">No locations specified.</div></div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Status Details */}
                    {/* <div className="col-12">
                        <div className="p-20 radius-12 border bg-white shadow-sm transition-hover">
                            <h6 className="mb-12 border-bottom pb-8 d-flex align-items-center gap-2 text-primary-600 fw-bold" style={{ fontSize: '15px' }}>
                                <Icon icon="solar:clipboard-list-bold-duotone" /> Status History & Details
                            </h6>
                            <div className="table-responsive scroll-sm">
                                <table className="table bordered-table sm-table mb-0">
                                    <thead>
                                        <tr style={{ background: "linear-gradient(135deg, #000000, #4f1818, #ed1c24)" }}>
                                            <th scope="col" style={{ color: "white", fontWeight: "bold", fontSize: "12px", width: "60px" }}>S.No</th>
                                            <th scope="col" style={{ color: "white", fontWeight: "bold", fontSize: "12px", width: "130px" }}>Changed By</th>
                                            <th scope="col" style={{ color: "white", fontWeight: "bold", fontSize: "12px", width: "150px" }}>Date & Time</th>
                                            <th scope="col" style={{ color: "white", fontWeight: "bold", fontSize: "12px", minWidth: "250px" }}>Remarks</th>
                                            <th scope="col" style={{ color: "white", fontWeight: "bold", fontSize: "12px", width: "120px" }}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {client.statusDetails && client.statusDetails.length > 0 ? (
                                            client.statusDetails.map((item, index) => (
                                                <tr key={index}>
                                                    <td className="text-xs">{index + 1}</td>
                                                    <td className="text-xs fw-semibold text-secondary-light">
                                                        {item.createdByName || "-"}
                                                    </td>
                                                    <td className="text-xs text-secondary-light">
                                                        {formatDateTime(item.date)}
                                                    </td>
                                                    <td className="text-xs text-dark fw-medium" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                                                        {item.remarks || "-"}
                                                    </td>
                                                    <td className="text-xs">
                                                        <span 
                                                            className={`badge px-12 py-4 radius-pill fw-bold ${
                                                                item.status === 'Converted' ? 'bg-success-focus text-success-main' :
                                                                item.status === 'High Intent' ? '' :
                                                                item.status === 'In Discussions' ? 'bg-warning-focus text-warning-main' :
                                                                item.status === 'Not Interested' ? 'bg-danger-focus text-danger-main' :
                                                                item.status === 'Potential' ? 'bg-info-focus text-info-main' :
                                                                'bg-neutral-100 text-neutral-600'
                                                            }`}
                                                            style={item.status === 'High Intent' ? {
                                                                backgroundColor: "#f3e8ff",
                                                                color: "#6d28d9",
                                                                border: "1px solid #ddd6fe"
                                                            } : {}}
                                                        >
                                                            {item.status || "-"}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center py-3 text-secondary-light italic text-xs">
                                                    No status history found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div> */}
                </div>
            </div>

            <style jsx>{`
                .transition-hover {
                    transition: all 0.3s ease;
                }
                .transition-hover:hover {
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
                    transform: translateY(-2px);
                }
                .detail-item {
                    border-left: 3px solid #ed1c24;
                    padding-left: 12px;
                }
            `}</style>
        </div>
    );
};

export default ClientViewLayer;
